import { logger } from "../../debug";
import { parseCookies } from "../../generalUtils";
import eventHandlers from "./eventHandlers";
import hightouch from "./hightouch";
import { transformEvent } from "./transformation";
import { IdentifyEvent, PageEvent, TrackEvent } from "./types/hightouch";
import { LAM2 } from "./types/lam";

function enrichEvent(data: LAM2): LAM2 {
  const cookies = parseCookies();
  const geoGata = cookies["lll_edge_geo_data"];
  if (geoGata) {
    data.payload.user.geoIp = {
      ...data.payload.user.geoIp,
      zip: geoGata.zip || data.payload.user.geoIp.zip,
      region: geoGata.region || data.payload.user.geoIp.region,
      country: geoGata.country || data.payload.user.geoIp.country,
      city: geoGata.city || data.payload.user.geoIp.city,
    };
  }
  return data;
}

function getSfmcId(data: LAM2) {
  const params = new URLSearchParams(data.context.document.location.search);
  return params.get("mcid");
}

function createIdentifyEvent(
  data: LAM2,
  anonymousId: string,
  traits: Record<string, string>
): IdentifyEvent {
  const baseEvent = transformEvent(data, anonymousId);
  return {
    ...baseEvent,
    traits,
  };
}

async function handleEvent(data: LAM2, anonymousId: string) {
  const eventName = data.payload.event.eventName;
  const handler = eventHandlers[eventName];

  if (handler) {
    logger.debug(`[Hightouch plugin] Handling ${eventName}`);
    const enriched = enrichEvent(data);
    const result = handler.handler(enriched, anonymousId);

    if (!result) {
      logger.error(
        `[Hightouch plugin] Could not map ${eventName} to Hightouch event`
      );
      return;
    }

    const calls = [];

    if (handler.type === "page") {
      calls.push(hightouch.page(result as PageEvent));
    } else if (handler.type === "identify") {
      calls.push(hightouch.identify(result as IdentifyEvent));
    } else if (handler.type === "track") {
      calls.push(hightouch.track(result as TrackEvent));
    }

    const sfmcId = getSfmcId(enriched);
    if (sfmcId) {
      const event = createIdentifyEvent(enriched, anonymousId, { sfmcId });
      calls.push(hightouch.identify(event));
    }

    return Promise.all(calls);
  }
}

export default (config: {
  enabled?: boolean;
  name?: string;
  debug?: boolean;
  isAllowed?: () => boolean;
}) => {
  const { name, enabled, isAllowed } = config;

  return {
    name: name ?? "hightouch",
    config,
    enabled: enabled ?? false,
    loaded: () => true,
    ready: () => true,
    track: ({ payload, instance }) => {
      if (isAllowed && !isAllowed()) {
        return;
      }

      const { properties, event } = payload;
      const anonymousId = instance.user("anonymousId");

      if (
        event === "automatedData" &&
        properties.validationResult.success === true
      ) {
        handleEvent(
          payload.properties.validationResult.data,
          anonymousId
        ).catch((e) => {
          logger.error(e);
        });
      } else if (event === "automatedData") {
        const eventName = properties?.payload?.event?.eventName;
        logger.warn(
          `[Hightouch plugin] Could not handle ${eventName} event`,
          properties
        );
      }
    },
  };
};
